import { Component, OnInit } from '@angular/core';
import { finalize, forkJoin } from 'rxjs';
import { AmazonFinanceValidationResult } from 'gung-standard';
import {
  JeevesConfiguration,
  JeevesReference,
  JeevesReferenceValidation
} from '../../../models/jeeves-amazon-finance.models';
import { JeevesAmazonFinanceService } from '../../../services/jeeves-amazon-finance/jeeves-amazon-finance.service';

type JeevesMapField = 'countryAndTaxPercentToTaxCode' | 'marketplaceToWarehouse';

@Component({
  selector: 'gung-jeeves-amazon-finance-configuration',
  templateUrl: './amazon-finance-jeeves-configuration.component.html',
  styleUrl: './amazon-finance-jeeves-configuration.component.scss',
  standalone: false
})
export class AmazonFinanceJeevesConfigurationComponent implements OnInit {
  jeeves: JeevesConfiguration | null = null;
  validation: AmazonFinanceValidationResult | null = null;
  referenceValidation: JeevesReferenceValidation | null = null;
  mapText = {} as Record<JeevesMapField, string>;
  referenceQuery = '';
  referenceType: 'customers' | 'products' = 'customers';
  references: JeevesReference[] = [];
  loading = false;
  saving = false;
  searching = false;
  saved = false;
  error = '';

  constructor(private readonly jeevesFinanceService: JeevesAmazonFinanceService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    forkJoin({
      configuration: this.jeevesFinanceService.getConfiguration(),
      references: this.jeevesFinanceService.validateReferences()
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: result => {
          this.jeeves = result.configuration.configuration;
          this.validation = result.configuration.validation;
          this.referenceValidation = result.references;
          this.populateTextFields();
        },
        error: () => (this.error = 'JEEVES_FINANCE_CONFIGURATION_LOAD_ERROR')
      });
  }

  validate(): void {
    const prepared = this.prepareConfiguration();
    if (!prepared) return;
    this.jeevesFinanceService.validateConfiguration(prepared).subscribe({
      next: validation => (this.validation = validation),
      error: () => (this.error = 'AMAZON_FINANCE_VALIDATION_ERROR')
    });
  }

  save(): void {
    const prepared = this.prepareConfiguration();
    if (!prepared) return;
    this.saving = true;
    this.saved = false;
    this.error = '';
    this.jeevesFinanceService.validateConfiguration(prepared).subscribe({
      next: validation => {
        this.validation = validation;
        if (validation.errors.length) {
          this.saving = false;
          return;
        }
        this.jeevesFinanceService
          .saveConfiguration(prepared)
          .pipe(finalize(() => (this.saving = false)))
          .subscribe({
            next: result => {
              this.jeeves = result.configuration;
              this.validation = result.validation;
              this.saved = true;
              this.populateTextFields();
              this.refreshReferenceValidation();
            },
            error: error => (this.error = error?.error?.message || 'AMAZON_FINANCE_SAVE_ERROR')
          });
      },
      error: () => {
        this.saving = false;
        this.error = 'AMAZON_FINANCE_VALIDATION_ERROR';
      }
    });
  }

  searchReferences(): void {
    if (!this.referenceQuery.trim()) return;
    this.searching = true;
    const request =
      this.referenceType === 'customers'
        ? this.jeevesFinanceService.findCustomers(this.referenceQuery)
        : this.jeevesFinanceService.findProducts(this.referenceQuery);
    request.pipe(finalize(() => (this.searching = false))).subscribe({
      next: references => (this.references = references),
      error: () => (this.error = 'JEEVES_REFERENCE_SEARCH_ERROR')
    });
  }

  refreshReferenceValidation(): void {
    this.jeevesFinanceService.validateReferences().subscribe({
      next: validation => (this.referenceValidation = validation)
    });
  }

  private prepareConfiguration(): JeevesConfiguration | null {
    if (!this.jeeves) return null;
    this.error = '';
    try {
      return {
        ...this.jeeves,
        countryAndTaxPercentToTaxCode: this.parseMap(this.mapText.countryAndTaxPercentToTaxCode),
        marketplaceToWarehouse: this.parseMap(this.mapText.marketplaceToWarehouse)
      };
    } catch (error) {
      this.error = error instanceof Error ? error.message : 'AMAZON_FINANCE_CONFIGURATION_PARSE_ERROR';
      return null;
    }
  }

  private populateTextFields(): void {
    if (!this.jeeves) return;
    this.mapText.countryAndTaxPercentToTaxCode = this.mapToText(this.jeeves.countryAndTaxPercentToTaxCode);
    this.mapText.marketplaceToWarehouse = this.mapToText(this.jeeves.marketplaceToWarehouse);
  }

  private mapToText(map: Record<string, number>): string {
    return Object.entries(map || {})
      .map(([key, value]) => `${key} = ${value}`)
      .join('\n');
  }

  private parseMap(value: string): Record<string, number> {
    const result: Record<string, number> = {};
    (value || '')
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .forEach((line, index) => {
        const separator = line.indexOf('=');
        if (separator < 1) throw new Error(`${'INVALID_MAPPING_LINE'} ${index + 1}: ${line}`);
        const key = line.slice(0, separator).trim();
        const rawValue = line.slice(separator + 1).trim();
        const number = Number(rawValue);
        if (!key || !rawValue) throw new Error(`${'INVALID_MAPPING_LINE'} ${index + 1}: ${line}`);
        if (!Number.isFinite(number)) throw new Error(`${'INVALID_NUMBER_ON_LINE'} ${index + 1}: ${rawValue}`);
        result[key] = number;
      });
    return result;
  }
}
